import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import logo from "@/assets/apex-av-new-logo-optimized.webp";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

type LetterBrand = { name: string; logo_url: string | null; logo_height: number };

export const Route = createFileRoute("/tools/letter")({
  head: () => ({
    meta: [
      { title: "New Homeowner Letter | Apex AV Tools" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: LetterTool,
});

const DEFAULT_BODY = `Welcome to your new home! Moving in is the perfect moment to plan the technology that will make daily life easier, quieter, and more enjoyable for years to come.

Apex AV is a locally owned, licensed and insured smart home integrator based right here in the Denver metro. We design and install premium audio, video, networking, and automation systems for homeowners who want it done once, and done right.

Whether you're finishing a basement theater, running clean network cable before the drywall goes up, or simply want your TVs mounted and Wi-Fi that actually works everywhere — we'd love to earn your business.

Mention this letter for a complimentary in-home consultation and design walkthrough. There's no obligation, just honest advice from a team that lives and works in your neighborhood.`;

const BRANDS = [
  "Sonos", "Klipsch", "Episode", "Marantz", "Denon",
  "Sony", "LG", "Samsung", "Sanus",
  "eero", "Ubiquiti", "Araknis Networks",
  "ecobee", "Luma", "WattBox",
];

const SERVICES = [
  "Custom Home Theater",
  "Whole-Home Audio & Video",
  "Smart Lighting & Shading",
  "Networking & Wi-Fi",
  "Security & Surveillance",
  "New Construction Prewire",
  "TV Mounting & Retrofit",
  "Climate & Voice Control",
];

function LetterTool() {
  const [greeting, setGreeting] = useState("Dear New Neighbor,");
  const [body, setBody] = useState(DEFAULT_BODY);
  const [signerName, setSignerName] = useState("The Apex AV Team");
  const [signerTitle, setSignerTitle] = useState("Denver's Smart Home Specialists");
  const [letterBrands, setLetterBrands] = useState<LetterBrand[]>([]);
  const today = new Date().toLocaleDateString("en-US", {
    year: "numeric", month: "long", day: "numeric",
  });

  useEffect(() => {
    supabase
      .from("brands")
      .select("name, logo_url, logo_height")
      .eq("enabled", true)
      .eq("include_on_letter", true)
      .order("sort_order", { ascending: true })
      .then(({ data }) => {
        if (data) setLetterBrands(data as LetterBrand[]);
      });
  }, []);


  return (
    <div>
      <div className="print:hidden">
        <h1 className="font-display text-3xl font-bold">New Homeowner Letter</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Customize below, then print or save as PDF. The letter fits on a single US Letter page. In the print
          dialog set margins to <strong>None</strong> and turn off <strong>Headers and footers</strong> so the
          browser doesn&apos;t add the page title, URL, or date.
        </p>


        <div className="mt-6 grid gap-4 rounded-lg border border-border bg-card p-5 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label htmlFor="greeting">Greeting</Label>
            <Input id="greeting" value={greeting} onChange={(e) => setGreeting(e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="body">Letter body</Label>
            <Textarea id="body" value={body} onChange={(e) => setBody(e.target.value)} rows={10} />
          </div>
          <div>
            <Label htmlFor="signer">Signed by</Label>
            <Input id="signer" value={signerName} onChange={(e) => setSignerName(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="title">Title / Tagline</Label>
            <Input id="title" value={signerTitle} onChange={(e) => setSignerTitle(e.target.value)} />
          </div>
        </div>

        <div className="mt-4 flex gap-3">
          <Button onClick={() => window.print()}>Print / Save as PDF</Button>
        </div>
      </div>

      {/* Printable letter */}
      <div className="mt-8 print:mt-0 flex justify-center">
        <div className="letter-page bg-white text-black shadow-lg print:shadow-none">
          <div className="letter-inner">
            {/* Header */}
            <header className="flex items-start justify-between border-b-2 border-[#1a1a2e] pb-4">
              <div className="flex items-center gap-3">
                <img src={logo} alt="Apex AV" className="h-16 w-auto" />
                <div>
                  <div className="font-display text-2xl font-bold tracking-tight text-[#1a1a2e]">
                    Apex AV
                  </div>
                  <div className="text-[11px] uppercase tracking-widest text-[#6b46c1]">
                    Denver Smart Home Integration
                  </div>
                </div>
              </div>
              <div className="text-right text-[10.5px] leading-snug text-neutral-700">
                <div className="font-semibold text-[#1a1a2e]">Apex AV</div>
                <div>Denver, CO</div>
                <div>(303) 317-3233</div>
                <div>info@apexav.com</div>
                <div>apexav.com</div>
              </div>
            </header>

            {/* Date */}
            <div className="mt-5 text-[11px] text-neutral-600">{today}</div>

            {/* Greeting + body */}
            <div className="mt-4">
              <p className="text-[12px] font-semibold text-[#1a1a2e]">{greeting}</p>
              <div className="mt-3 space-y-3 text-[11.5px] leading-relaxed text-neutral-800">
                {body.split(/\n+/).map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </div>

            {/* Services */}
            <div className="mt-5">
              <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-[#6b46c1]">
                What we do
              </div>
              <ul className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-neutral-800">
                {SERVICES.map((s) => (
                  <li key={s} className="flex items-start gap-1.5">
                    <span className="mt-[5px] inline-block h-1 w-1 shrink-0 rounded-full bg-[#6b46c1]" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Brands */}
            <div className="mt-4">
              <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-[#6b46c1]">
                Authorized dealer for
              </div>
              {letterBrands.filter((b) => b.logo_url).length > 0 ? (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                  {letterBrands
                    .filter((b) => b.logo_url)
                    .map((b) => (
                      <img
                        key={b.name}
                        src={b.logo_url as string}
                        alt={b.name}
                        style={{ height: Math.min(b.logo_height || 20, 24) }}
                        className="w-auto object-contain"
                      />
                    ))}
                </div>
              ) : (
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10.5px] font-medium text-neutral-700">
                  {BRANDS.map((b, i) => (
                    <span key={b} className="inline-flex items-center gap-3">
                      <span>{b}</span>
                      {i < BRANDS.length - 1 && <span className="text-neutral-400">·</span>}
                    </span>
                  ))}
                </div>
              )}
            </div>


            {/* Signature */}
            <div className="mt-6">
              <p className="text-[11.5px] text-neutral-800">Warm regards,</p>
              <p className="mt-4 text-[12px] font-semibold text-[#1a1a2e]">{signerName}</p>
              <p className="text-[10.5px] text-neutral-600">{signerTitle}</p>
            </div>

            {/* Footer */}
            <footer className="mt-auto border-t border-neutral-300 pt-3 text-center text-[9.5px] text-neutral-500">
              Licensed · Insured · Locally Owned &nbsp;·&nbsp; Serving the Denver Metro Area &nbsp;·&nbsp; apexav.com &nbsp;·&nbsp; (303) 317-3233
            </footer>
          </div>
        </div>
      </div>

      <style>{`
        .letter-page {
          width: 8.5in;
          height: 11in;
          padding: 0.6in 0.75in;
          box-sizing: border-box;
        }
        .letter-inner {
          display: flex;
          flex-direction: column;
          height: 100%;
        }
        @media print {
          @page { size: letter; margin: 0; }
          html, body {
            background: #fff !important;
            margin: 0 !important;
            padding: 0 !important;
            height: auto !important;
          }
          /* Remove all site chrome from the printed page */
          header, footer, nav, aside, .print\\:hidden { display: none !important; }
          body * { visibility: hidden !important; }
          .letter-page, .letter-page * { visibility: visible !important; }
          .letter-page {
            position: fixed !important;
            left: 0 !important;
            top: 0 !important;
            margin: 0 !important;
            box-shadow: none !important;
            page-break-after: avoid;
            break-after: avoid;
          }
        }

      `}</style>
    </div>
  );
}
